"use client";

import Banner from "../shared/Banner";
import DownloadIcon from "../shared/icons/DownloadIcon";
import TitleDescAndSideItems from "../shared/titleDescAndSideItems/TitleDescAndSideItems";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import StickyNavComponent from "./StickyNavComponent";
import useMediaGetter from "@/app/hooks/useMediaGetter";
import { TPartner } from "@/app/types/homePageTypes";
import { useRouter, useSearchParams } from "next/navigation";
import { screenSizeContext } from "@/app/store/context/screenSizesContext";
import { loadingContext } from "@/app/store/context/isLoadingContext";
import dynamic from "next/dynamic";

const NeighborhoodsDetailsSwiper = dynamic(
	() => import("./NeighborhoodsDetailsSwiper"),
	{
		loading: () => (
			<div
				className="h-30rem md:h-40rem min-h-[30rem] md:min-h-[40rem]"
				aria-hidden
			/>
		),
	}
);

const InnerMasterPlan = dynamic(() => import("./InnerMasterPlan"), {
	ssr: false,
	loading: () => (
		<div
			className="min-h-[44.88rem] lg:min-h-[66.4375rem] bg-oBlue-150"
			aria-hidden
		/>
	),
});

const CircleWithTextComponent = dynamic(
	() => import("../shared/CircleWithText/CircleWithTextComponent"),
	{
		ssr: false,
		loading: () => (
			<div className="min-h-[40rem] lg:min-h-screen bg-white" aria-hidden />
		),
	}
);

const FinishingComponent = dynamic(() => import("./FinishingComponent"), {
	loading: () => (
		<div className="min-h-[30rem] lg:min-h-[40rem] bg-[#F1F2F3]" aria-hidden />
	),
});

const Partners = dynamic(() => import("../shared/partners/Partners"), {
	loading: () => <div className="min-h-[20rem] bg-white" aria-hidden />,
});

const GetInTouch = dynamic(() => import("../shared/form/GetInTouch"), {
	loading: () => (
		<div id="get_in_touch" className="min-h-[32rem] bg-oBlue-100" aria-hidden />
	),
});

const NeighborhoodsDetailsPopups = dynamic(
	() => import("./NeighborhoodsDetailsPopups"),
	{ ssr: false }
);

interface Option {
	value: string;
	label: string;
}

const NeighborhoodsDetailsComponent = ({
	brochure,
	data,
	destinationData,
}: any) => {
	const { isPageFetched, setIsPageFetched } = useContext(loadingContext);
	const { isIpad, isMobile, isReady } = useContext(screenSizeContext);

	const params = useSearchParams();
	const booking = params.get("book");

	const [selectedNeighborhood, setSelectedNeighborhood] =
		useState<Option | null>(null);

	const { imgSrc, videoSrc } = useMediaGetter(
		data?.sections?.banners?.[0]?.media
	);
	const [activeUnitCard, setActiveUnitCard] = useState(false);
	const [activeUnitCardData, setActiveUnitCardData] = useState<any>();
	const [masterplanSelectedUnit, setMasterplanSelectedUnit] =
		useState<string>("");
	const [, setSelectedInquireUnit] = useState(null);
	const [animationDone, setAnimationDone] = useState(false);
	const router = useRouter();

	const partnerData = useMemo<TPartner[]>(() => {
		if (!data?.sections?.our_partners?.[0]?.children) return [];

		return data.sections.our_partners[0].children.map((partner: any) => ({
			imgSrc:
				process.env.NEXT_PUBLIC_STATIC_MEDIA_API_URL +
				partner?.logo?.media_name,
		}));
	}, [data?.sections?.our_partners]);

	const overviewRef = useRef<HTMLDivElement | null>(null);
	const galleryRef = useRef<HTMLDivElement | null>(null);
	const perksRef = useRef<HTMLDivElement | null>(null);
	const masterPlanRef = useRef<HTMLDivElement | null>(null);
	const nearbyRef = useRef<HTMLDivElement | null>(null);
	const finishingRef = useRef<HTMLDivElement | null>(null);
	const partnersRef = useRef<HTMLDivElement | null>(null);
	const getInTouchRef = useRef<HTMLDivElement | null>(null);
	const dirBack = useRef<number>(0);

	const TabsData = [
		{ id: 1, title: "Overview", value: "overview", ref: overviewRef },
		...(data?.sections?.gallery?.[0]?.media
			? [{ id: 2, title: "Gallery", value: "gallery", ref: galleryRef }]
			: []),
		{ id: 3, title: "MasterPlan", value: "masterPlan", ref: masterPlanRef },
		{ id: 4, title: "Perks", value: "perks", ref: perksRef },
		...(data?.sections?.live_close?.[0]?.children
			? [{ id: 5, title: "Nearby", value: "nearby", ref: nearbyRef }]
			: []),
		...(data?.sections?.finishing_specs?.[0]?.title?.en
			? [
					{
						id: 6,
						title: "Finishing Specifications",
						value: "finishing",
						ref: finishingRef,
					},
				]
			: []),
		{ id: 7, title: "Partners", value: "partners", ref: partnersRef },
		{ id: 8, title: "Get In Touch", value: "getInTouch", ref: getInTouchRef },
	];

	const handleInquireUnitClick = (unit: any) => {
		setSelectedInquireUnit(unit);
	};

	const removeParam = (paramName: string) => {
		const p = new URLSearchParams(params.toString());
		p.delete(paramName);

		const newUrl = p.toString() ? `?${p.toString()}` : "";
		router.push(`${window.location.pathname}${newUrl}`, { scroll: false });
	};

	useEffect(() => {
		setSelectedNeighborhood({
			value: data?.page?.id,
			label: data?.page?.name?.en,
		});
	}, [data?.page?.id, data?.page?.name?.en]);

	useEffect(() => {
		if (!activeUnitCard) {
			window.lenis?.startScroll?.(true);
		}
	}, [activeUnitCard]);

	useEffect(() => {
		if (!isReady || !booking || !animationDone) return;

			const banner = document.querySelector(".banner") as HTMLElement;
			const form = document.getElementById("get_in_touch");
			if (!form) return;

			if (!isIpad) {
				window?.lenis?.scrollTo(form.offsetTop + banner?.offsetHeight - 40, {
					force: true,
					duration: 2,
					easing: (x) =>
						x < 0.5 ? 8 * x * x * x * x : 1 - Math.pow(-2 * x + 2, 4) / 2,
					onComplete() {
						window.lenis?.startScroll?.(true);
						removeParam("book");
					},
				});
			} else {
				void (async () => {
					document.documentElement.style.overflow = "auto";
					const { default: gsap } = await import("gsap");
					const { ScrollToPlugin } = await import("gsap/ScrollToPlugin");
					gsap.registerPlugin(ScrollToPlugin);
					gsap.to(window, {
						scrollTo: { y: form.offsetTop + banner?.offsetHeight - 60 },
					});
					removeParam("book");
				})();
			}
	}, [booking, animationDone, isIpad, isReady, params, router]);

	useEffect(() => {
		if (imgSrc) {
			setTimeout(() => {
				setIsPageFetched((prev: string) => {
					if (prev === "loading") return "fetched";
					return prev;
				});
			});
		}
	}, [imgSrc, setIsPageFetched]);

	const tl = useRef<GSAPTimeline | null>(null);

	useEffect(() => {
		if (isPageFetched === "fetched" && imgSrc) {
			let cancelled = false;
			let stickyTrigger: { kill: () => void } | undefined;

			const timer = setTimeout(async () => {
				const { default: gsap } = await import("gsap");
				const { ScrollTrigger } = await import("gsap/ScrollTrigger");
				if (cancelled) return;

				gsap.registerPlugin(ScrollTrigger);

				const html = document.documentElement;

				tl.current?.kill();
				tl.current = gsap.timeline();
				tl.current
					.timeScale(html.classList.contains("loaded") ? 1.25 : 1)
					.set(
						".banner .banner-button, .banner .hero-title-word, .banner .banner-head-icon, .banner-head-desc",
						{ yPercent: 50, alpha: 0 }
					)
					.set(".stickyTabs_wrapper .sticky", { yPercent: 100 })
					.set(".banner .hero-video", {
						clipPath: "inset(50% 50% 50% 50%)",
						scale: 0.75,
						transformOrigin: "center center",
					})
					.to(".banner .hero-image img", {
						delay: html.classList.contains("loaded") ? 0 : 1.6,
						duration: 1,
						ease: "power2.inOut",
						y: "0%",
					})
					.to(
						".banner .hero-image-wrapper",
						{
							duration: 1,
							ease: "power2.inOut",
							scale: 1,
						},
						"-=0.25"
					)
					.to(
						".navbar_section",
						{
							duration: 1,
							ease: "power2.inOut",
							y: "0%",
						},
						"-=0.4"
					)
					.to(
						".banner .hero-video",
						{
							duration: 1,
							ease: "expo.inOut",
							scale: 1,
							clipPath: "inset(0% 0% 0% 0%)",
						},
						"<"
					)
					.to(
						".banner .banner-button, .banner .hero-title-word, .banner .banner-head-icon, .banner-head-desc",
						{
							duration: 1,
							stagger: 0.05,
							ease: "expo.inOut",
							yPercent: 0,
							alpha: 1,
						},
						"<"
					)
					.to(
						".stickyTabs_wrapper .sticky",
						{
							duration: 1,
							stagger: 0.05,
							ease: "expo.out",
							yPercent: 0,
						},
						"<"
					)
					.call(() => {
						setAnimationDone(true);
					});

				stickyTrigger = ScrollTrigger.create({
					trigger: ".content-wrapper",
					start: "top top",
					end: "bottom top",
					pin: ".stickyTabs_wrapper",
					pinSpacing: false,
					onEnter: () => {
						gsap.to(".navbar_section", { background: "#0d223d" });
					},
					onLeaveBack: () => {
						gsap.to(".navbar_section", { background: "transparent" });
						gsap.killTweensOf(".stickyTabs_wrapper .sticky");
						gsap.set(".stickyTabs_wrapper .sticky", { yPercent: 0 });
					},
					onUpdate: ({ direction }) => {
						if (direction === 1) {
							dirBack.current = 1;
							gsap.to(".navbar_section", { yPercent: -100 });
							gsap.to(".stickyTabs_wrapper .sticky", { yPercent: 0 });
						} else {
							dirBack.current = -1;
							gsap.to(".navbar_section", { yPercent: 0 });
							gsap.to(".stickyTabs_wrapper .sticky", { yPercent: -100 });
						}
					},
				});
			}, 50);

			return () => {
				cancelled = true;
				clearTimeout(timer);
				tl.current?.kill();
				stickyTrigger?.kill();
			};
		}
	}, [isPageFetched, imgSrc]);
	console.log(data?.page?.gallery , data?.sections?.gallery ,"data?.sections?.gallery");
	
	return (
		<div className={`page_wrapper ${isIpad && "neigborhoods-details"}`}>
			{imgSrc ? (
				<Banner
					priority
					details
					minHeight="86vh"
					mobileHeight="95vh"
					imageSrc={`${imgSrc}`}
					title={data?.sections?.banners?.[0]?.title?.en}
					videoSrc={videoSrc}
					description={data?.sections?.banners?.[0]?.description?.en}
					buttonText="Download brochure"
					buttonIcon={<DownloadIcon color="white" />}
					btnAction={() => {
						const mediaUrl = `${process.env.NEXT_PUBLIC_STATIC_MEDIA_API_URL}${brochure.media_name}`;
						window.open(mediaUrl, "_blank");
					}}
					zoneIconImg={
						data?.sections?.banners?.[0]?.logo?.media_name &&
						process.env.NEXT_PUBLIC_STATIC_MEDIA_API_URL +
							data?.sections?.banners?.[0]?.logo?.media_name
					}
					hasBrochure={brochure?.media_name}
					optimizedImage
				/>
			) : (
				<div className="h-[86vh] mb-[3rem]"></div>
			)}
			<div className="content-wrapper || relative bg-white">
				<div className={`stickyTabs_wrapper || relative`}>
					<StickyNavComponent TabsData={TabsData} />
				</div>
				<div ref={overviewRef}>
					<TitleDescAndSideItems
						title={data?.sections?.neighborhood_overview?.[0]?.title?.en}
						description={
							data?.sections?.neighborhood_overview?.[0]?.description?.en
						}
						items={data?.sections?.neighborhood_overview?.[0]?.children}
						CustomClass="pt-11 lg:pt-[6.25rem]"
						optimizedImage
					/>
				</div>
				<div
					ref={galleryRef}
					className="h-30rem md:h-40rem"
					style={{ minHeight: isMobile ? "30rem" : "40rem" }}
				>
					{data?.page?.gallery && (
						<NeighborhoodsDetailsSwiper
							images={data?.page?.gallery}
							paddingBottom={
								!data?.sections?.life_in_the_details?.[0]?.children
							}
							optimizedImage
						/>
					)}
				</div>
				<div className="" ref={masterPlanRef}>
					<InnerMasterPlan
						backgroundMedia={data?.page?.master_plan?.media}
						units={data?.page?.unit_designs || []}
						facilities={data?.page?.master_plan?.facilities || []}
						handleInquireUnitClick={handleInquireUnitClick}
						setActiveUnitCard={setActiveUnitCard}
						setActiveUnitCardData={setActiveUnitCardData}
						sectionDescription={data?.page?.master_plan?.description?.["en"]}
						optimizedImage
					/>
				</div>
				<div ref={perksRef}>
					{data?.sections?.life_in_the_details?.[0]?.children && (
						<CircleWithTextComponent
							data={data?.sections?.life_in_the_details?.[0]?.children}
							text={data?.sections?.life_in_the_details?.[0]?.title?.en}
							customClass="lifeSection !pb-[2.5rem] lg:mb-[11rem]"
							imgSrc={imgSrc}
							optimizedImage
						/>
					)}
				</div>

				{data?.sections?.live_close?.[0]?.children && (
					<div ref={nearbyRef}>
						<TitleDescAndSideItems
							title={data?.sections?.live_close?.[0]?.title?.en}
							description={data?.sections?.live_close?.[0]?.description?.en}
							items={data?.sections?.live_close?.[0]?.children}
							optimizedImage
						/>
					</div>
				)}
				{data?.sections?.finishing_specs?.[0]?.title?.en && (
					<div ref={finishingRef}>
						<FinishingComponent
							data={data?.sections?.finishing_specs?.[0]}
							list={data?.sections?.finishing_specs_tabs}
							optimizedImage
						/>
					</div>
				)}
				<div ref={partnersRef}>
					<Partners
						data={partnerData}
						title={data?.sections?.our_partners?.[0]?.title?.en}
						imgSrc={imgSrc}
						neighborhoodsDetails
						optimizedImage
						titleClassName="text-bodyM leading-[110%] tracking-[-0.015rem] lg:text-h5 lg:leading-[100%] lg:tracking-[-0.06rem]"
						paddingTop={
							!data?.sections?.live_close?.[0]?.children &&
							!data?.sections?.finishing_specs?.[0]?.title?.en
								? true
								: false
						}
					/>
				</div>
				<div ref={getInTouchRef}>
					<GetInTouch
						destinationData={destinationData}
						NeighborhoodsDetailsPage
						selectedNeighborhood={selectedNeighborhood}
						setSelectedNeighborhood={setSelectedNeighborhood}
						slug={data?.page?.slug}
						masterplanSelectedUnit={masterplanSelectedUnit}
						setMasterplanSelectedUnit={setMasterplanSelectedUnit}
					/>
				</div>
			</div>
			{activeUnitCard && (
				<NeighborhoodsDetailsPopups
					activeUnitCardData={activeUnitCardData}
					setActiveUnitCard={setActiveUnitCard}
					setMasterplanSelectedUnit={setMasterplanSelectedUnit}
					optimizedImage
				/>
			)}
		</div>
	);
};

export default NeighborhoodsDetailsComponent;
